import MenuIcon from '@mui/icons-material/Menu';
import {AppBar, Box, IconButton, Stack, Theme, Toolbar, Typography} from '@mui/material';
import {forwardRef} from 'react';
import {ComponentBoundary} from '../../common/ComponentBoundary';
import {useResponsiveAppName} from '../../common/hooks/useAppName';
import {useTranslation} from '../../i18n/hooks/useTranslation';
import {Translation} from '../../i18n/Translation';
import Logo from '../../logo.svg?react';
import {DropDownMenu} from '../../navigation/DropDownMenu';
import {RouteButton} from '../../navigation/RouteButton';
import {RouteLink} from '../../navigation/RouteLink';
import {AppUpdateNotification} from '../../pwa/AppUpdateNotification';
import {PropsWithoutChildren} from '../../react/types';
import {useDeviceState} from '../../responsive/hooks/useDeviceState';
import {Paths} from '../../routes/paths';
import {LogoWidth} from '../constants';
import {PublicNavigationMenu} from './PublicNavigationMenu';

export interface PublicTopBarProps extends PropsWithoutChildren {
  handleNavigationMenuClick: () => void;
  hideLoginButton?: boolean;
  hideSignupButton?: boolean;
}

export const PublicTopBar = forwardRef<HTMLDivElement, PublicTopBarProps>(
  ({handleNavigationMenuClick, hideLoginButton, hideSignupButton}: PublicTopBarProps, ref) => {
    const appName = useResponsiveAppName();
    const {isNarrow} = useDeviceState();
    const {translate} = useTranslation('main');
    const hasHiddenButton = hideLoginButton || hideSignupButton;
    const buttonColor = hasHiddenButton ? 'secondary' : 'primary';
    return (
      <AppBar
        color="inherit"
        component="header"
        data-testid="PublicTopBar"
        position="fixed"
        ref={ref}
        role="banner"
        sx={{zIndex: (theme: Theme) => theme.zIndex.drawer + 1}}
      >
        <Toolbar disableGutters sx={{mr: 2}}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Stack direction="row" spacing={isNarrow ? 0 : 4} sx={{alignItems: 'center'}}>
              {isNarrow && (
                <IconButton
                  aria-label={translate('main:menu')}
                  color="inherit"
                  edge="start"
                  onClick={handleNavigationMenuClick}
                  size="large"
                  sx={{mx: 2, p: 0}}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <RouteLink
                color="inherit"
                sx={{alignItems: 'center', display: 'flex', flexDirection: 'row'}}
                to={Paths.Welcome}
                underline="none"
              >
                {!isNarrow && (
                  <Box sx={{alignItems: 'center', display: 'flex', justifyContent: 'center', mx: 2}}>
                    <Logo width={LogoWidth} />
                  </Box>
                )}
                <Typography variant="h6">{appName}</Typography>
              </RouteLink>
              {!isNarrow && (
                <Translation keys={['main:navigation.pubic.menu.label', 'main:navigation.pubic.menu.title']}>
                  {([productMenuLabel, productMenuTitle]) => (
                    <DropDownMenu
                      color="info"
                      label={productMenuLabel}
                      title={<Typography variant="h6">{productMenuTitle}</Typography>}
                      variant="text"
                    >
                      <PublicNavigationMenu orientation="horizontal" />
                    </DropDownMenu>
                  )}
                </Translation>
              )}
            </Stack>
            <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
              <ComponentBoundary>
                <AppUpdateNotification />
              </ComponentBoundary>
              {!hideLoginButton && (
                <Typography variant="subtitle1">
                  <Translation keys="auth:login">
                    {(login) => (
                      <RouteButton
                        color={buttonColor}
                        to={Paths.Login}
                        variant={hasHiddenButton ? 'contained' : 'outlined'}
                      >
                        {login}
                      </RouteButton>
                    )}
                  </Translation>
                </Typography>
              )}
              {!hideSignupButton && (
                <Typography variant="subtitle1">
                  <Translation keys="auth:signup">
                    {(signup) => (
                      <RouteButton color={buttonColor} to={Paths.Signup} variant="contained">
                        {signup}
                      </RouteButton>
                    )}
                  </Translation>
                </Typography>
              )}
            </Stack>
          </Box>
        </Toolbar>
      </AppBar>
    );
  },
);
PublicTopBar.displayName = 'PublicTopBar';
